import axios from 'axios';
import { client } from '@/services/api/axios.ts';
import { useQuery } from '@tanstack/react-query';

export type TOverviewBESS = {
  batterySoC: number;
  batterySoH: number;
  batteryPowerStatusCharge: number;
  batteryPowerStatusDischarge: number;
  batteryPowerStatus:
    | 'Unknown'
    | 'Idle'
    | 'Charging'
    | 'Discharging'
    | 'Charging and Discharging';
  batteryTemperature: number;
};

export async function getOverviewBESS({
  id,
}: {
  id: string | undefined;
}): Promise<TOverviewBESS | undefined> {
  if (!id) return undefined;

  try {
    const { data } = await client.get<{ body: TOverviewBESS }>(`/bess/${id}/overview`);

    return data?.body;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(`${error?.response?.data?.message}`);
    }

    if (error instanceof Error) {
      throw new Error(`${error.message}}`);
    }
  }
}

export const useOverviewBESSQuery = ({ id }: { id?: string }) => {
  return useQuery({
    queryKey: ['bess', id, 'overview'],
    queryFn: () => getOverviewBESS({ id }),
    retry: 1,
    staleTime: 1000 * 60,
  });
};

//
//
//
//
//

export type TOverviewCellsStatusMBESS = {
  availableCellModules: number | null;
  balancingMode: number | null;
  minimumVoltageCell: number | null;
  maximumVoltageCell: number | null;
};

export async function getOverviewCellsStatusBESS({
  id,
}: {
  id: string | undefined;
}): Promise<TOverviewCellsStatusMBESS | undefined> {
  if (!id) return undefined;

  const searchParams = new URLSearchParams();
  searchParams.set('status', 'cells');

  try {
    const { data } = await client.get<{ body: TOverviewCellsStatusMBESS }>(
      `/bess/${id}/overview?${searchParams.toString()}`
    );

    return data?.body;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(`${error?.response?.data?.message}`);
    }

    if (error instanceof Error) {
      throw new Error(`${error.message}}`);
    }
  }
}

export const useCellStatusBESSQuery = ({ id }: { id?: string }) => {
  return useQuery({
    queryKey: ['bess', id, 'overview', 'status', 'cells'],
    queryFn: () => getOverviewCellsStatusBESS({ id }),
    retry: 1,
    staleTime: 1000 * 60,
  });
};

//
//
//
//
//

export type TOverviewCellsModuleMBESS = {
  minimumVoltageCell: number | null;
  maximumVoltageCell: number | null;

  cells: {
    id: number;
    voltage: number;
    temperature: number;
  }[];
};

export async function getOverviewModuleBESS({
  id,
  moduleNumber,
}: {
  id: string | undefined;
  moduleNumber: number | undefined;
}): Promise<TOverviewCellsModuleMBESS | undefined> {
  if (!id || !moduleNumber) return undefined;

  const searchParams = new URLSearchParams();
  if (moduleNumber) {
    searchParams.set('moduleNumber', String(moduleNumber));
  }

  try {
    const { data } = await client.get<{ body: TOverviewCellsModuleMBESS }>(
      `/bess/${id}/overview?${searchParams.toString()}`
    );

    return data?.body;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(`${error?.response?.data?.message}`);
    }

    if (error instanceof Error) {
      throw new Error(`${error.message}}`);
    }
  }
}

export const useOverviewModuleBESSQuery = ({
  id,
  moduleNumber,
}: {
  id?: string;
  moduleNumber?: number;
}) => {
  return useQuery({
    queryKey: ['bess', id, 'overview', 'module', moduleNumber],
    queryFn: () => getOverviewModuleBESS({ id, moduleNumber }),
    retry: 1,
    staleTime: 1000 * 60,
  });
};
